import type { KeyboardEvent } from 'react';

/**
 * Shared keyboard-activation helper for non-native interactive elements.
 *
 * Native controls (`<button>`, Fluent `Button`, `MenuItem`, etc.) already
 * activate on Enter/Space. Elements that only carry an `onClick` — a `div`,
 * a Fluent `Card`, a clickable table row/header — do NOT, so keyboard users
 * cannot operate them. Wire this into `onKeyDown` alongside the `onClick`.
 *
 * It fires the handler on Enter and Space, and calls `preventDefault()` so
 * Space does not scroll the page (and Enter does not submit an enclosing form).
 *
 * @example
 *   <div
 *     role="button"
 *     tabIndex={0}
 *     onClick={handleActivate}
 *     onKeyDown={handleActivateKeyDown(handleActivate)}
 *   />
 */
export function handleActivateKeyDown<T extends HTMLElement = HTMLElement>(
  handler: (event: KeyboardEvent<T>) => void
): (event: KeyboardEvent<T>) => void {
  return (event: KeyboardEvent<T>) => {
    // ' ' is the modern key value; 'Spacebar' covers older engines.
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      handler(event);
    }
  };
}

/** ARIA `aria-sort` values for a sortable column header. */
export type AriaSort = 'ascending' | 'descending' | 'none';

/**
 * Maps this app's internal sort-direction representation
 * (`'asc' | 'desc' | null`) to the corresponding `aria-sort` token so
 * assistive tech announces the current sort state of a column header.
 */
export function toAriaSort(direction: 'asc' | 'desc' | null | undefined): AriaSort {
  if (direction === 'asc') return 'ascending';
  if (direction === 'desc') return 'descending';
  return 'none';
}
